import { useEffect, useRef, useState } from 'react';
import type { SceneAssets, MobileBehavior } from './manifest';
import { safeImageUrl } from '../../renderer/url-safety';

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

interface SceneMediaProps {
  assets: SceneAssets;
  mobileBehavior: MobileBehavior;
  className?: string;
}

export function SceneMedia({ assets, mobileBehavior, className = '' }: SceneMediaProps) {
  const isMobile = useIsMobileViewport();
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const wantVideo =
    assets.renderStrategy === 'video_primary' &&
    assets.primaryVideo &&
    !(isMobile && mobileBehavior.disableVideo);

  const primaryImageUrl = safeImageUrl(assets.primaryImage.publicUrl);
  const primaryVideoUrl = wantVideo && !videoFailed ? safeImageUrl(assets.primaryVideo!.publicUrl) : null;

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {/* Video layer */}
      {primaryVideoUrl && (
        <video
          key={`video-${primaryVideoUrl}`}
          muted
          loop
          playsInline
          autoPlay
          poster={primaryImageUrl}
          onError={() => setVideoFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={primaryVideoUrl} type={assets.primaryVideo?.mimeType || 'video/mp4'} />
        </video>
      )}

      {/* Image layer (fallback from video or primary) */}
      {!primaryVideoUrl || videoFailed ? (
        imageFailed ? (
          // Gradient fallback (style-pack color via CSS variable)
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              background: `var(--scene-gradient, linear-gradient(135deg, rgba(200,150,100,0.1), rgba(100,150,200,0.1)))`,
            }}
          />
        ) : (
          <img
            src={primaryImageUrl}
            alt="ceremony scene"
            onError={() => setImageFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )
      ) : null}
    </div>
  );
}
